import { useQuery } from '@tanstack/react-query';
import { PkButton } from '@uefadigital/panenka-react';
import parseHtmlToReact from 'html-react-parser';
import { useMemo } from 'react';

import type { HospitalityOptions } from './types';

import { getEditorialsQuery, getEditorialByIdQuery } from '@queries/editorial';
import PictureHelper from '@shared/PictureHelper';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { language } from '@utilities/browser';
import { competitionId } from '@utilities/WindowContext';

import * as styles from './styles.module.scss';

const HospitalityCategoryDetails = ({ options }: { options: HospitalityOptions }) => {
  const articleFilters = useMemo(
    () => ({
      type: 'article',
      'param.attributes.main.kind': options.kind,
      'param.attributes.sections': options.section,
      'param.attributes.footballEntities.venue.venueId': options.venueId,
      'param.attributes.hashtags': options.hashtag,
      'param.attributes.language': language,
      'param.attributes.main.competition': competitionId,
      aggregator: 'lightnodejson',
    }),
    [options],
  );

  const handleClick = () => {
    GoogleTagManagerHelper.event(
      'Click',
      'Apply',
      'hospitality',
      { interaction: 'Apply', section_reference: 'hospitality' },
      'hospitality_apply',
    );
  };

  const { data: articles } = useQuery({
    ...getEditorialsQuery(articleFilters),
  });

  const articleId = articles?.[0]?.id;
  const { data: article } = useQuery({
    ...getEditorialByIdQuery(articleId),
    enabled: !!articleId,
  });

  if (!article) return null;

  const boxAndCtaText = article?.body
    .filter((o) => o._type === 'EXTRA')
    .flatMap((p) => p._json.filter((c) => c._type !== 'EXTRA'))
    .map((j) => j._html);

  const btnText = article?.body
    .filter((o) => o._type === 'EXTRA')
    .flatMap((p) => p._json.filter((c) => c._type === 'EXTRA'))
    .flatMap((j) => j._json)
    .map((z) => z._html);

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>{article.title}</div>
      <div className={styles.venue}>{article.description}</div>
      <div className={styles.content}>
        <div className={styles.imageWrapper}>
          <PictureHelper
            formats={['0@w1']}
            image={article.mainMedia?.image}
            title={article.title}
            className={styles.image}
          />
        </div>
        <div className={styles.table}>
          {article.body.filter((o) => o._type === 'TABLE').map((p) => parseHtmlToReact(p._html))}
        </div>
        <div className={styles.boxWrapper}>
          <div className={styles.box}>
            <div className={styles.boxText}>
              {boxAndCtaText.map((j, k) => k < 2 && <div key={k}>{parseHtmlToReact(j)}</div>)}
            </div>

            <div className={styles.cta}>
              <div className={styles.ctaText}>
                {boxAndCtaText.map((j, k) => k > 1 && <div key={k}>{parseHtmlToReact(j)}</div>)}
              </div>
              {btnText.map(
                (z, li) =>
                  li === 0 && (
                    <div className={styles.button} key={li}>
                      <PkButton width='auto' onPkClick={handleClick}>
                        {parseHtmlToReact(z)}
                      </PkButton>
                    </div>
                  ),
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HospitalityCategoryDetails;
